import React, { useState, useEffect } from 'react';
import styles from './token.module.scss';
import { API_BASE_URL } from '../../api';

const TokenAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/token/analytics/`, {
        headers: {
          'Authorization': `Token ${localStorage.getItem('authToken')}`,
        },
      });

      const data = await response.json();

      if (data.status === 'success') {
        setAnalytics(data.data);
      } else {
        setError(data.message || 'Failed to fetch analytics');
      }
    } catch (err) {
      setError('Error fetching analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading analytics...</div>;
  }

  if (error) {
    return <div className={styles.errorMessage}>Error: {error}</div>;
  }

  if (!analytics) {
    return <div className={styles.noData}>No analytics data available</div>;
  }

  const { statistics, recent_searches } = analytics;

  return (
    <div className={styles.analyticsContainer}>
      <div className={styles.analyticsHeader}>
        <h2>📊 टोकन प्रयोग विश्लेषण</h2>
        <p>टोकन खोज र प्रयोगको विस्तृत विश्लेषण</p>
      </div>

      {/* Statistics Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>🔍</div>
          <div className={styles.statContent}>
            <h3>कुल खोजहरू</h3>
            <div className={styles.statNumber}>{statistics.total_searches}</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>✅</div>
          <div className={styles.statContent}>
            <h3>सफल खोजहरू</h3>
            <div className={styles.statNumber}>{statistics.successful_searches}</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>❌</div>
          <div className={styles.statContent}>
            <h3>असफल खोजहरू</h3>
            <div className={styles.statNumber}>{statistics.failed_searches}</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>📈</div>
          <div className={styles.statContent}>
            <h3>सफलता दर</h3>
            <div className={styles.statNumber}>{statistics.success_rate.toFixed(1)}%</div>
          </div>
        </div>
      </div>

      {/* Success Rate Chart */}
      <div className={styles.chartSection}>
        <h3>सफलता दर</h3>
        <div className={styles.progressBar}>
          <div 
            className={styles.progressFill}
            style={{ width: `${statistics.success_rate}%` }}
          ></div>
        </div>
        <div className={styles.chartLabels}>
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Recent Searches */}
      <div className={styles.recentSearches}>
        <h3>हालैका खोजहरू</h3>
        <div className={styles.tableContainer}>
          <table className={styles.analyticsTable}>
            <thead>
              <tr>
                <th>टोकन</th>
                <th>IP ठेगाना</th>
                <th>खोज परिणाम</th>
                <th>संस्था</th>
                <th>खोज मिति</th>
              </tr>
            </thead>
            <tbody>
              {recent_searches.map((search) => (
                <tr key={search.id} className={styles[search.search_result]}>
                  <td className={styles.tokenCell}>
                    <code>{search.token}</code>
                  </td>
                  <td>{search.ip_address}</td>
                  <td>
                    <span className={`${styles.resultBadge} ${styles[search.search_result]}`}>
                      {search.search_result_display}
                    </span>
                  </td>
                  <td>{search.institution_name}</td>
                  <td>{new Date(search.created_at).toLocaleString('ne-NP')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insights */}
      <div className={styles.insightsSection}>
        <h3>💡 अन्तर्दृष्टि</h3>
        <div className={styles.insightsGrid}>
          <div className={styles.insightCard}>
            <h4>सफलता दर</h4>
            <p>
              {statistics.success_rate >= 80 ? 'उत्कृष्ट' : 
               statistics.success_rate >= 60 ? 'राम्रो' : 
               statistics.success_rate >= 40 ? 'मध्यम' : 'सुधार आवश्यक'}
            </p>
          </div>
          
          <div className={styles.insightCard}>
            <h4>खोज प्रवृत्ति</h4>
            <p>
              {statistics.total_searches > 100 ? 'धेरै सक्रिय प्रयोग' :
               statistics.total_searches > 50 ? 'मध्यम प्रयोग' : 'कम प्रयोग'}
            </p>
          </div>
          
          <div className={styles.insightCard}>
            <h4>सुझाव</h4>
            <p>
              {statistics.success_rate < 60 ? 'टोकन प्रणाली सुधार गर्नुपर्छ' :
               statistics.success_rate < 80 ? 'थोरै सुधार आवश्यक' : 'प्रणाली राम्रोसँग काम गर्दैछ'}
            </p>
          </div>
        </div>
      </div>

      {/* Export Options */}
      <div className={styles.exportSection}>
        <h3>📤 निर्यात विकल्पहरू</h3>
        <div className={styles.exportButtons}>
          <button className={styles.exportButton}>
            📊 Excel मा निर्यात गर्नुहोस्
          </button>
          <button className={styles.exportButton}>
            📄 PDF मा निर्यात गर्नुहोस्
          </button>
          <button className={styles.exportButton}>
            📅 मासिक रिपोर्ट
          </button>
        </div>
      </div>
    </div>
  );
};

export default TokenAnalytics;
